import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/common/Button/Button';
import ClearFiltersButton from '../../components/common/ClearFiltersButton/ClearFiltersButton';
import DataTable from '../../components/common/DataTable/DataTable';
import DatePresetFilter from '../../components/common/DatePresetFilter/DatePresetFilter';
import EmptyState from '../../components/common/EmptyState/EmptyState';
import FilterBar from '../../components/common/FilterBar/FilterBar';
import FilterChipsBar, { type FilterChipEntry } from '../../components/common/FilterChipsBar/FilterChipsBar';
import FilterGroup from '../../components/common/FilterGroup/FilterGroup';
import ListingBottomRow from '../../components/common/ListingBottomRow/ListingBottomRow';
import Loading from '../../components/common/Loading/Loading';
import MultiSelectFilter from '../../components/common/MultiSelectFilter/MultiSelectFilter';
import RecordCount from '../../components/common/RecordCount/RecordCount';
import SearchFilterBar from '../../components/common/SearchFilterBar/SearchFilterBar';
import { PAYMENTS_RECEIVED_PERIOD_PRESETS, detectDatePreset } from '../../constants/datePresets';
import { usePaymentsReceivedDashboard } from '../../hooks/usePaymentsReceivedDashboard';
import { queryFilter, useQueryFilters } from '../../hooks/useQueryFilters';
import { PaymentsReceivedService } from '../../services/PaymentsReceivedService';
import { PAYMENT_SYSTEM_LABELS, type PaymentSystem } from '../../types/payment';
import { formatDateTime } from '../../utils';
import {
  PAYMENTS_RECEIVED_PERIODS,
  formatPaymentAmount,
  resolvePaymentsReceivedQuery,
  type PaymentsReceivedPeriod,
} from '../../utils/paymentsReceivedDashboard';
import {
  buildPaymentsReceivedCsv,
  buildPaymentsReceivedJson,
  buildPaymentsReceivedPdf,
  downloadBinaryFile,
  downloadTextFile,
  paymentsReceivedExportFileName,
  toPaymentsReceivedExportRows,
  type PaymentsReceivedExportFormat,
} from '../../utils/paymentsReceivedExport';
import dashboardStyles from './Dashboard.module.css';
import styles from './PaymentsReceivedPanel.module.css';

const PAGE_SIZE_OPTIONS = [10, 20, 50] as const;
const GATEWAY_FILTER_VALUES = ['asaas', 'stripe', 'paypal', 'pix'] as const;
type PaymentsReceivedGateway = (typeof GATEWAY_FILTER_VALUES)[number];

const HEADERS = ['Aluna', 'Pagador', 'Plano', 'Pago em', 'Gateway', 'Valor'];
const GRID_TEMPLATE = 'minmax(180px, 1.4fr) minmax(140px, 1fr) minmax(140px, 1fr) 160px 120px 120px';

const PAYMENTS_RECEIVED_FILTER_SCHEMA = {
  period: queryFilter.enum<PaymentsReceivedPeriod>(
    'today',
    PAYMENTS_RECEIVED_PERIODS,
    { param: 'periodo' },
  ),
  dateStart: queryFilter.string('', { param: 'de', trim: true }),
  dateEnd: queryFilter.string('', { param: 'ate', trim: true }),
  searchName: queryFilter.string(''),
  paymentGateway: queryFilter.array<PaymentsReceivedGateway>([], { allowed: GATEWAY_FILTER_VALUES }),
  page: queryFilter.integer(1, { min: 1 }),
  pageSize: queryFilter.integer(20, { allowed: PAGE_SIZE_OPTIONS }),
};

const isPaymentsReceivedPeriod = (value: string): value is PaymentsReceivedPeriod => (
  (PAYMENTS_RECEIVED_PERIODS as readonly string[]).includes(value)
);

const isPaymentsReceivedGateway = (value: string): value is PaymentsReceivedGateway => (
  (GATEWAY_FILTER_VALUES as readonly string[]).includes(value)
);

const gatewayLabel = (value: string | null): string => {
  if (!value) {
    return '—';
  }

  if (Object.prototype.hasOwnProperty.call(PAYMENT_SYSTEM_LABELS, value)) {
    return PAYMENT_SYSTEM_LABELS[value as PaymentSystem];
  }

  return value;
};

const PaymentsReceivedPanel: React.FC = () => {
  const navigate = useNavigate();
  const { filters, setFilter, setFilters, resetFilters } = useQueryFilters(
    PAYMENTS_RECEIVED_FILTER_SCHEMA,
    { pageKey: 'page' },
  );
  const resolved = useMemo(
    () => resolvePaymentsReceivedQuery(
      filters.period,
      filters.dateStart,
      filters.dateEnd,
      filters.searchName,
      filters.paymentGateway,
      filters.page,
      filters.pageSize,
    ),
    [
      filters.dateEnd,
      filters.dateStart,
      filters.page,
      filters.pageSize,
      filters.paymentGateway,
      filters.period,
      filters.searchName,
    ],
  );
  const query = resolved.ok ? resolved.query : null;
  const { snapshot, loading, error } = usePaymentsReceivedDashboard(query);
  const totalCount = snapshot?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / filters.pageSize));
  const page = Math.min(filters.page, totalPages);
  const payments = snapshot?.payments ?? [];
  const isDefaultPeriod = filters.period === 'today' && !filters.dateStart && !filters.dateEnd;
  const hasActiveFilters = !isDefaultPeriod
    || Boolean(filters.searchName.trim())
    || filters.paymentGateway.length > 0;

  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  const gatewayOptions = useMemo(() => GATEWAY_FILTER_VALUES.map((value) => ({
    value,
    label: PAYMENT_SYSTEM_LABELS[value],
  })), []);

  const chips: FilterChipEntry[] = useMemo(() => filters.paymentGateway.map((value) => ({
    filterKey: 'paymentGateway',
    value,
    label: PAYMENT_SYSTEM_LABELS[value],
    onRemove: () => setFilter(
      'paymentGateway',
      filters.paymentGateway.filter((item) => item !== value),
    ),
  })), [filters.paymentGateway, setFilter]);

  useEffect(() => {
    if (!exportOpen) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!exportMenuRef.current?.contains(event.target as Node)) {
        setExportOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [exportOpen]);

  const handlePeriodChange = (start: string, end: string) => {
    const detected = detectDatePreset(start, end, PAYMENTS_RECEIVED_PERIOD_PRESETS);
    if (detected === 'custom' || !isPaymentsReceivedPeriod(detected)) {
      setFilters({ period: 'custom', dateStart: start, dateEnd: end });
      return;
    }

    setFilters({ period: detected, dateStart: '', dateEnd: '' });
  };

  const handleExport = async (format: PaymentsReceivedExportFormat) => {
    if (!query) {
      return;
    }

    setExportOpen(false);
    setExporting(true);
    setExportError(null);

    try {
      const allPayments = await PaymentsReceivedService.getAllForExport({
        dateStart: query.dateStart,
        dateEnd: query.dateEnd,
        searchName: query.searchName,
        paymentGateway: query.paymentGateway,
      });
      const rows = toPaymentsReceivedExportRows(allPayments);
      const fileName = paymentsReceivedExportFileName(format, query.dateStart, query.dateEnd);

      if (format === 'csv') {
        downloadTextFile(buildPaymentsReceivedCsv(rows), fileName, 'text/csv;charset=utf-8');
      } else if (format === 'json') {
        downloadTextFile(buildPaymentsReceivedJson(rows), fileName, 'application/json;charset=utf-8');
      } else {
        downloadBinaryFile(buildPaymentsReceivedPdf(rows), fileName, 'application/pdf');
      }
    } catch (caught: unknown) {
      setExportError(caught instanceof Error ? caught.message : 'Não foi possível exportar os pagamentos.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <section className={styles.panel}>
      <FilterBar gap="sm" compact pushFirstChild className={dashboardStyles.filtersRow}>
        <RecordCount label="Pagamentos:" count={totalCount} />
        <FilterGroup>
          <SearchFilterBar
            searchValue={filters.searchName}
            onSearchChange={(value) => setFilter('searchName', value)}
            searchPlaceholder="Nome ou e-mail (aluna ou pagador)"
            loading={loading}
          />
        </FilterGroup>
        <FilterGroup>
          <DatePresetFilter
            dateStart={query?.dateStart ?? filters.dateStart}
            dateEnd={query?.dateEnd ?? filters.dateEnd}
            onChange={handlePeriodChange}
            placeholder="Período"
            presetOptions={PAYMENTS_RECEIVED_PERIOD_PRESETS}
          />
        </FilterGroup>
        <FilterGroup>
          <MultiSelectFilter
            values={filters.paymentGateway}
            onChange={(values) => setFilter('paymentGateway', values.filter(isPaymentsReceivedGateway))}
            options={gatewayOptions}
            placeholder="Gateway"
            clearButtonLabel="Limpar gateway"
            hideChips
          />
        </FilterGroup>
        {hasActiveFilters && (
          <ClearFiltersButton onClick={() => resetFilters()} />
        )}
        <div className={styles['export-wrap']} ref={exportMenuRef}>
          <Button
            type="button"
            variant="outline"
            size="small"
            icon="download"
            iconRight="arrow_drop_down"
            disabled={!resolved.ok || exporting || loading}
            onClick={() => setExportOpen((open) => !open)}
          >
            {exporting ? 'Exportando...' : 'Exportar'}
          </Button>
          {exportOpen && (
            <div className={styles['export-menu']} role="menu">
              <button type="button" role="menuitem" onClick={() => handleExport('pdf')}>
                PDF
              </button>
              <button type="button" role="menuitem" onClick={() => handleExport('csv')}>
                CSV
              </button>
              <button type="button" role="menuitem" onClick={() => handleExport('json')}>
                JSON
              </button>
            </div>
          )}
        </div>
      </FilterBar>

      <FilterChipsBar chips={chips} compact />

      {!resolved.ok && <EmptyState message={resolved.message} showImage={false} />}
      {resolved.ok && loading && !snapshot && <Loading />}
      {resolved.ok && !loading && error && <EmptyState message={error} showImage={false} />}
      {resolved.ok && exportError && <EmptyState message={exportError} showImage={false} />}
      {resolved.ok && !loading && !error && snapshot && totalCount === 0 && (
        <EmptyState
          message={hasActiveFilters
            ? 'Nenhum pagamento encontrado com os filtros selecionados.'
            : 'Nenhuma aluna pagou neste período.'}
          showImage={false}
        />
      )}
      {resolved.ok && !error && snapshot && totalCount > 0 && (
        <>
          <DataTable
            headers={HEADERS}
            gridTemplate={GRID_TEMPLATE}
            lastColumnIsActions={false}
            borderedRows
            boldHeaders
          >
            {payments.map((row) => (
              <DataTable.Row key={`${row.paymentHistoryId}-${row.studentId}`}>
                <div className={styles['student-cell']}>
                  <button
                    type="button"
                    className={styles['name-link']}
                    onClick={() => navigate(`/alunas/${row.studentId}/editar?section=payments`)}
                  >
                    {row.studentName || '—'}
                  </button>
                  {row.studentEmail ? (
                    <span className={styles['student-email']}>{row.studentEmail}</span>
                  ) : null}
                </div>
                <div>{row.payerName || '—'}</div>
                <div>{row.planName || '—'}</div>
                <div className={styles.nowrap}>{formatDateTime(row.paidAt)}</div>
                <div>{gatewayLabel(row.paymentSystem)}</div>
                <div className={styles.nowrap}>{formatPaymentAmount(row.value, row.currency)}</div>
              </DataTable.Row>
            ))}
          </DataTable>
          <ListingBottomRow
            pageSize={filters.pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageSizeChange={(event) => {
              const value = Number.parseInt(event.target.value, 10);
              if (PAGE_SIZE_OPTIONS.some((option) => option === value)) {
                setFilter('pageSize', value);
              }
            }}
            pagination={{
              page,
              pageSize: filters.pageSize,
              totalRecords: totalCount,
              totalPages,
              hasNextPage: page < totalPages,
              hasPreviousPage: page > 1,
            }}
            loading={loading || exporting}
            onPageChange={(nextPage) => setFilter('page', nextPage, { resetPage: false })}
          />
        </>
      )}
    </section>
  );
};

export default PaymentsReceivedPanel;
